import { Inline, Stack } from '@/ui/line/line';
import { LinkIcon } from '@/ui/link/link-icon';
import { Logo } from '@/ui/logo/logo';
import { colors } from '@/theme/colors';
import { prepareLink } from '@/utils/link';
import { EllipsisTextTooltip } from '@/ui/table/infinite-table/ellipsis-text-tooltip';
import { TablePrimaryCell } from '@/ui/table/infinite-table/table-cells';
import { Flexbox } from '@/ui/flexbox/flexbox';
import { IconButton } from '@/ui/button/icon-button';
import { paths } from '@/routes/helpers/paths';
import { openNewTab } from '@/helpers/open-new-tab';
import { formatLocation } from '@/helpers/format-location';
import { SmallText } from '@/ui/typography/widgets';
import { Tooltip } from '@/ui/tooltip/tooltip';
import { useTranslation } from 'react-i18next';

type PendingCompanyBaseInfoCellProps = {
  data: {
    name: string;
    crunchbaseId?: string | null;
    website?: string | null;
    logoUrl?: string | null;
    organizationId?: string | null;
    hqLocation?: {
      city: { id: string; name: string } | null;
      region: { id: string; name: string } | null;
      country: { id: string; name: string } | null;
    } | null;
    linkedInUrl?: string | null;
  };
};

export const PendingCompanyBaseInfoCell = ({
  data,
}: PendingCompanyBaseInfoCellProps) => {
  const { t } = useTranslation('companies');

  const hqLocation = data.hqLocation;
  const formattedLocation = hqLocation
    ? formatLocation(
        {
          cityName: hqLocation.city?.name,
          regionName: hqLocation.region?.name,
          countryName: hqLocation.country?.name,
        },
        { compact: true },
      )
    : '';

  const locationText = formattedLocation || t`companies.noLocation`;

  const sayyyPath = data.organizationId
    ? paths.company({
        companyId: data.organizationId,
      })
    : '';

  return (
    <TablePrimaryCell>
      <Flexbox justify="space-between" gap="16px" fullWidth>
        <Flexbox gap="12px">
          <Logo
            name={data.name || ''}
            logoUrl={data.logoUrl || undefined}
            singleLetter
            bgColor={data.logoUrl ? 'transparent' : colors.accent.green.c3}
            border={data.logoUrl ? `2px solid ${colors.gray.c3}` : undefined}
          />
          <Stack gap="4px">
            <Inline gap="10px" justifyContent="flex-start" alignItems="center">
              <EllipsisTextTooltip
                text={data.name || ''}
                Component={
                  data.crunchbaseId ? (
                    <a
                      href={prepareLink(data.website || '')}
                      target="_blank"
                      rel="noreferrer"
                      data-testid="pending-company-name"
                    >
                      {data.name || ''}
                    </a>
                  ) : (
                    <span data-testid="pending-company-name">
                      {data.name || ''}
                    </span>
                  )
                }
              />
              {data.organizationId && (
                <IconButton
                  icon="VectorUp"
                  variant="ghost"
                  color={colors.basics.black}
                  onClick={() => {
                    openNewTab(sayyyPath);
                  }}
                />
              )}
            </Inline>
            <Tooltip
              disabled={!formattedLocation}
              content={formatLocation({
                cityName: hqLocation?.city?.name,
                regionName: hqLocation?.region?.name,
                countryName: hqLocation?.country?.name,
              })}
            >
              <SmallText>{locationText}</SmallText>
            </Tooltip>
          </Stack>
        </Flexbox>
        <Flexbox gap="8px" alignItems="center">
          <Tooltip content={data.website}>
            <LinkIcon
              disableTooltip
              url={data.website ?? ''}
              icon="Website"
              color={data.website ? '#007AFF' : '#D6DDF8'}
            />
          </Tooltip>
          <Tooltip content={data.linkedInUrl}>
            <LinkIcon
              disableTooltip
              url={data.linkedInUrl ?? ''}
              icon="LinkedIn"
              color={data.linkedInUrl ? '#0A66C2' : '#D6DDF8'}
            />
          </Tooltip>
        </Flexbox>
      </Flexbox>
    </TablePrimaryCell>
  );
};
